import { AddressField, BasicAddressDetails } from '@insurgrid/common';
import { useFormContext } from 'react-hook-form';
import { useEffect, useState } from 'react';
import { AddressInput } from './AddressInput';
import { Input } from './Input';
import { FormSelect } from './Select';

const PROPERTY_TYPE_OPTIONS = [
  { label: 'Primary', value: 'Primary' },
  { label: 'Rental', value: 'Rental' },
  { label: 'Vacation', value: 'Vacation' },
  { label: 'Secondary', value: 'Secondary' },
  { label: 'Other', value: 'Other' },
];

export function AddressForm({
  autocomplete = true,
  nickname = true,
  ...props
}: AddressField & {
  defaultValue?: BasicAddressDetails;
  autocomplete?: boolean;
  nickname?: boolean;
}) {
  const { setValue } = useFormContext();
  const [isFormPrefilled, setIsFormPrefilled] = useState(!autocomplete);

  useEffect(() => {
    if (autocomplete) {
      setIsFormPrefilled(
        !!(
          props.defaultValue?.street &&
          props.defaultValue?.city &&
          props.defaultValue?.usStateCode &&
          props.defaultValue?.postal
        ),
      );
    }
  }, [props.defaultValue, autocomplete]);

  const setFormValues = (autoFilledAddress: BasicAddressDetails) => {
    Object.entries(autoFilledAddress).forEach(([name, value]) => {
      setValue(`${props.name}.${name}`, value);
    });
    setIsFormPrefilled(true);
  };

  const nicknameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setValue(`${props.name}.nickname`, e.target.value);
  };

  return (
    <div className="space-y-3 sm:space-y-5">
      {autocomplete && (
        <AddressInput
          name="Address"
          items={[]}
          defaultValue={
            props.defaultValue?.street
              ? `${props.defaultValue?.street}, ${props.defaultValue?.city}, ${props.defaultValue?.usStateCode} ${props.defaultValue?.postal}`
              : undefined
          }
          label="Search your Address"
          onAddress={setFormValues}
          data-test-id="street1-dropdown"
        />
      )}

      <FormSelect
        data-test-id="property-nickname"
        label="Address type"
        options={PROPERTY_TYPE_OPTIONS}
        name={`${props.name}.nickname`}
        defaultValue={props.defaultValue?.nickname ?? undefined}
        className="w-full"
      />

      <Input
        name={`${props.name}.street`}
        defaultValue={props.defaultValue?.street ?? ''}
        label="Street"
        disabled={!isFormPrefilled}
        data-test-id={isFormPrefilled ? 'street1-filled' : 'street1-not-filled'}
      />
      <Input
        name={`${props.name}.street2`}
        defaultValue={props.defaultValue?.street2 ?? ''}
        label="Apt, suite, bldg"
        disabled={!isFormPrefilled}
        data-test-id="street2"
      />
      <Input
        name={`${props.name}.city`}
        defaultValue={props.defaultValue?.city ?? ''}
        label="City"
        disabled={!isFormPrefilled}
        data-test-id="city"
      />
      <div className="flex justify-between flex-1 w-full flex-grow-1 space-x-5">
        <div className="w-1/2">
          <Input
            name={`${props.name}.usStateCode`}
            defaultValue={props.defaultValue?.usStateCode ?? ''}
            label="State code"
            disabled={!isFormPrefilled}
            data-test-id="usStateCode"
          />
        </div>
        <div className="w-1/2">
          <Input
            name={`${props.name}.postal`}
            defaultValue={props.defaultValue?.postal ?? ''}
            label="Zip code"
            disabled={!isFormPrefilled}
            data-test-id="zipCode"
          />
        </div>
      </div>
    </div>
  );
}
